import { Dialog } from '@headlessui/react';
import { getAbsoluteZoom, getZoomFactor } from 'advanced-cropper/extensions/absolute-zoom';
import { forwardRef, JSX, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CropperRef } from 'react-advanced-cropper';

import { CloseIcon } from '~components/icons/CloseIcon';
import { CropIcon } from '~components/icons/CropIcon';
import { MinusIcon } from '~components/icons/MinusIcon';
import { PlusIcon } from '~components/icons/PlusIcon';
import { ThumbnailIcon } from '~components/icons/ThumbnailIcon';
import useLabels from '~hooks/useLabels';
import { Asset } from '~modules/media-library/model/Asset';
import { CropDownloadDto, CropFormatDto, CropFormatList } from '~modules/media-library/model/CropFormat';
import { Button } from '~modules/media-library/view/Button';
import { DownloadButton } from '~modules/media-library/view/CropModal/DownloadButton';
import { ImageCrop } from '~modules/media-library/view/CropModal/ImageCrop';
import { SelectCropFormat } from '~modules/media-library/view/CropModal/SelectCropFormat';
import { clamp } from '~utils/number';

type CropModalPanelProps = {
  asset: Asset;
  onClose?: () => void;
  cropFormats?: CropFormatList;
  cropFormat?: CropFormatDto;
  selectedCropFormat: number;
  onSelectCropFormat: (index: number) => void;
  onCustomWidthChanged: (width: number) => void;
  onCustomHeightChanged: (height: number) => void;
  onDownload?: (options: CropDownloadDto) => Promise<void>;
  isProcessing?: boolean;
  children?: ReactNode;
};

const SCALE_MAX = 0.8;
const SCALE_MIN = 0.1;

export const CropModalPanel = forwardRef<HTMLDivElement, JSX.IntrinsicElements['div'] & CropModalPanelProps>(
  function CropModalPanel(
    {
      asset,
      onClose,
      className,
      cropFormats = [],
      cropFormat = null,
      selectedCropFormat = 0,
      onSelectCropFormat,
      onCustomWidthChanged,
      onCustomHeightChanged,
      onDownload,
      isProcessing = false,
      children,
      ...TheirProps
    },
    ref,
  ) {
    const {
      hlImageCropDialog,
      labelImageCropDialogOriginalSize,
      // btnImageCropDialogReset,
      labelImageCropDialogSelectPreset,
      labelImageCropDialogCustomFormat,
    } = useLabels([
      'hlImageCropDialog',
      'labelImageCropDialogOriginalSize',
      // 'btnImageCropDialogReset',
      'labelImageCropDialogSelectPreset',
      'labelImageCropDialogCustomFormat',
    ]);
    const [croppedArea, setCroppedArea] = useState({ width: 0, height: 0, x: 0, y: 0 });
    const cropperRef = useRef<CropperRef>(null);
    const state = cropperRef?.current?.getState();
    const settings = cropperRef?.current?.getSettings();
    const absoluteZoom = getAbsoluteZoom(state, settings);

    const [isDragging, setIsDragging] = useState(false);

    // Handler: Click download button
    const handleDownloadClick = useCallback(() => {
      const image = document.querySelector('.advanced-cropper-background-image') as HTMLImageElement;

      if (image) {
        const containerWidth = image.naturalWidth;
        const containerHeight = image.naturalHeight;

        const { y, x, width, height } = croppedArea;

        const coordinatesInPercentage = {
          width: (width / containerWidth) * 100,
          height: (height / containerHeight) * 100,
          x: (x / containerWidth) * 100,
          y: (y / containerHeight) * 100,
        };

        const scaleFactor = 1;
        const x0 = clamp((50 + (coordinatesInPercentage.x - 50) * scaleFactor) / 100, 0, 1);
        const y0 = clamp((50 + (coordinatesInPercentage.y - 50) * scaleFactor) / 100, 0, 1);
        const x1 = clamp((coordinatesInPercentage.width * scaleFactor) / 100, 0, 1);
        const y1 = clamp((coordinatesInPercentage.height * scaleFactor) / 100, 0, 1);

        onDownload({
          assetId: asset.id,
          width: cropFormat.width,
          height: cropFormat.height,
          x0,
          y0,
          x1,
          y1,
        });
      }
    }, [asset.id, cropFormat, onDownload, croppedArea]);

    const handleZoom = zoomIn => {
      if (cropperRef) {
        cropperRef.current.zoomImage(
          getZoomFactor(state, settings, Math.min(1, zoomIn ? absoluteZoom + 0.1 : absoluteZoom - 0.1)),
          {
            transitions: true,
          },
        );
      }
    };

    const handleMouseDown = useCallback(() => {
      setIsDragging(true);
    }, []);

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Add event listeners to detect when dragging is happening
    useEffect(() => {
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }, [handleMouseUp]);

    const safeOnClose = useCallback(() => {
      if (!isDragging) {
        onClose?.();
      }
    }, [isDragging, onClose]);

    return (
      <div
        className={`ml--crop-modal--panel overflow-hidden rounded-md shadow-xl max-w-xl w-full  min-h-0 max-h-full flex flex-col relative bg-blue ${
          className ? className : ''
        }`}
        {...TheirProps}
        ref={ref}
        onMouseDown={handleMouseDown}
      >
        <header className="ml--crop-modal--panel-header pl-3 py-2 pr-8">
          <Dialog.Title
            as="h2"
            className="ml--crop-modal--title inline-flex m-0 h-8 font-sans text-xl leading-none items-center text-white"
          >
            <CropIcon className="w-9 mr-3" />
            {hlImageCropDialog}
          </Dialog.Title>
        </header>
        <Button
          variant="icon"
          className="absolute top-0 right-0 border-none bg-transparent text-white"
          onClick={safeOnClose}
        >
          <CloseIcon />
        </Button>
        <div className="ml--crop-modal--panel-body overflow-hidden text-left pb-3">
          <figure className="relative flex justify-center items-center w-full overflow-hidden transition bg-black bg-opacity-10 p-0 m-0">
            {!!asset.previews.details ? (
              <ImageCrop
                cropperRef={cropperRef}
                cropFormat={cropFormat}
                setCroppedArea={setCroppedArea}
                imgSrc={asset.previews.details}
                disabled={isProcessing}
              />
            ) : (
              <div className="w-1/3">
                <ThumbnailIcon className="text-black opacity-30 mt-2" />
              </div>
            )}

            <div className="absolute bottom-0 right-0 pr-3 pb-3 flex flex-col gap-2 bg-opacity-100">
              <Button variant="icon" onClick={() => handleZoom(true)} disabled={absoluteZoom >= SCALE_MAX} size="small">
                <PlusIcon />
              </Button>
              <Button
                variant="icon"
                onClick={() => handleZoom(false)}
                disabled={absoluteZoom <= SCALE_MIN}
                size="small"
              >
                <MinusIcon />
              </Button>
            </div>
          </figure>
          <div className="font-sans text-white flex justify-between p-3 items-center">
            <div className="font-bold leading-loose whitespace-nowrap mr-4">{labelImageCropDialogOriginalSize}</div>
            <span>{asset.attributes.width || '...'} px</span>
            <CloseIcon className="w-4 mx-2" />
            <span>{asset.attributes.height || '...'} px</span>
            <div className="flex-1" />
            {/*
            <Button
              variant="default"
              className=" inline-flex m-0 h-8 font-sans text-base leading-none items-center  my-2 mr-2"
              onClick={onClose}
            >
              <ArrowResetIcon className="mr-2" />
              {btnImageCropDialogReset}
            </Button> */}
          </div>
          {Boolean(cropFormat) ? (
            <div>
              {cropFormats.length > 1 && (
                <div className="font-sans text-white px-3 pb-3">
                  <div className="font-bold leading-loose whitespace-nowrap">{labelImageCropDialogSelectPreset}</div>
                  <SelectCropFormat
                    disabled={isProcessing}
                    cropFormats={cropFormats}
                    selected={selectedCropFormat}
                    onSelect={onSelectCropFormat}
                  />
                </div>
              )}
              {/* Custom Format */}
              <div className="font-sans text-white px-3 pb-3">
                <div className="font-bold leading-loose whitespace-nowrap">{labelImageCropDialogCustomFormat}</div>
                <div className="flex justify-start items-center">
                  <input
                    className="p-1 !rounded w-30"
                    type="number"
                    value={cropFormat.width}
                    onChange={e => {
                      onCustomWidthChanged(parseInt(e.target.value));
                    }}
                  />
                  <CloseIcon className="w-6 mx-2" />
                  <input
                    className="p-1 rounded w-30"
                    type="number"
                    value={cropFormat.height}
                    onChange={e => {
                      onCustomHeightChanged(parseInt(e.target.value));
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>loading</div>
          )}
        </div>
        <div className="ml--crop-modal--panel-footer flex justify-between items-center border-b-0 border-l-0 border-r-0 border-t border-solid border-white py-2 px-3">
          <div className="font-sans select-none text-white text-sm">{asset.attributes.legalNotice}</div>
          <div className="flex gap-2 h-8">
            <DownloadButton onClick={handleDownloadClick} isProcessing={isProcessing} />
          </div>
        </div>
        {/* This is only here for the self-clicking link component */}
        {children}
      </div>
    );
  },
);
